<template>
  <div class="content">
    <div class="pathway-builder-wrap">
      <card class="card no-border-card" no-body>
        <div class="pb">

          <!-- ============ TOP HEADER ============ -->
          <header class="pb-top">
            <div class="pb-top__left">
              <nav class="pb-crumbs">
                <a class="pb-crumb" @click="goBack">← Pathways</a>
                <span class="pb-crumb-sep">/</span>
                <span class="pb-crumb pb-crumb--current">{{ isEdit ? 'Edit pathway' : 'New pathway' }}</span>
              </nav>

              <div class="pb-title-row">
                <input
                  v-model="pathway.title"
                  class="pb-title-input"
                  placeholder="Untitled pathway"
                  maxlength="120"
                  @input="markDirty" />
                <span class="pb-status" :class="'pb-status--' + (pathway.status || 'draft')">
                  {{ statusLabel(pathway.status) }}
                </span>
              </div>

              <input
                v-model="pathway.subtitle"
                class="pb-subtitle-input"
                placeholder="Add modules and organize them into a meaningful learning sequence."
                maxlength="200"
                @input="markDirty" />

              <div v-if="showMetaRow" class="pb-meta-row">
                <div class="pb-meta-select-wrap">
                  <select
                    v-model="pathway.track"
                    class="pb-meta-select"
                    @change="markDirty">
                    <option value="">Track</option>
                    <option value="foundations">Foundations</option>
                    <option value="strategies">Strategies</option>
                    <option value="leadership">Leadership</option>
                    <option value="coaching">Coaching</option>
                  </select>
                  <svg class="pb-meta-select__chev" viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clip-rule="evenodd"/></svg>
                </div>
                <span class="pb-meta-status">{{ statusLabel(pathway.status) }}</span>
              </div>
            </div>

            <div class="pb-top__stats">
              <div class="pb-stat">
                <div class="pb-stat__value">{{ modules.length }}</div>
                <div class="pb-stat__label">Modules</div>
              </div>
              <div class="pb-stat">
                <div v-if="modules.length === 0" class="pb-stat__value pb-stat__value--muted">—</div>
                <div v-else class="pb-stat__value">{{ totalDuration }}<span class="pb-stat__unit">min</span></div>
                <div class="pb-stat__label">Est. time</div>
              </div>
              <div class="pb-stat">
                <div v-if="modules.length === 0" class="pb-stat__value pb-stat__value--muted">—</div>
                <div v-else class="pb-stat__value">{{ gatedCount }}<span class="pb-stat__unit">/{{ modules.length }}</span></div>
                <div class="pb-stat__label">Sequential</div>
              </div>
            </div>

            <div class="pb-top__actions">
              <span v-if="dirty" class="pb-saved pb-saved--dirty">Unsaved</span>
              <span v-else-if="savedAt" class="pb-saved pb-saved--ok">
                Saved just now
                <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" /></svg>
              </span>

              <button class="pb-btn pb-btn--ghost" @click="openPreview">
                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>
                Preview learner view
              </button>
              <button class="pb-btn pb-btn--primary" :disabled="saving" @click="saveDraft">
                Save draft
              </button>

              <div class="pb-kebab-wrap" v-click-outside="closeMenu">
                <button class="pb-btn pb-btn--icon" @click="menuOpen = !menuOpen" title="More options">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M10 4a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 4.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 4.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z"/></svg>
                </button>
                <div v-if="menuOpen" class="pb-menu" role="menu">
                  <button class="pb-menu__item" :disabled="!canPublish" @click="onMenuPublish">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd"/></svg>
                    {{ pathway.status === 'published' ? 'Update & republish' : 'Publish' }}
                  </button>
                  <button class="pb-menu__item" @click="onMenuDuplicate">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M7 3a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V5a2 2 0 00-2-2H7z"/><path d="M3 7a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>
                    Duplicate
                  </button>
                  <div class="pb-menu__sep"></div>
                  <button class="pb-menu__item pb-menu__item--danger" @click="onMenuArchive">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M2 5a2 2 0 012-2h12a2 2 0 110 4H4a2 2 0 01-2-2zm0 5a2 2 0 012-2h12a2 2 0 012 2v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5zm6 1a1 1 0 100 2h4a1 1 0 100-2H8z"/></svg>
                    Archive
                  </button>
                </div>
              </div>
            </div>
          </header>

          <!-- ============ 3-COLUMN BODY ============ -->
          <div class="pb-body">

            <!-- ----- LEFT: Course library ----- -->
            <aside class="pb-pane pb-pane--library">
              <div class="pb-pane__head">
                <h3 class="pb-pane__title">Course library</h3>
                <p class="pb-pane__sub">Drag items to canvas or click +</p>
              </div>

              <div class="pb-search-row">
                <div class="pb-search">
                  <svg class="pb-search__icon" viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/></svg>
                  <input
                    ref="librarySearchInput"
                    v-model="librarySearch"
                    class="pb-search__input"
                    placeholder="Search courses…" />
                </div>
                <button class="pb-icon-btn" title="Filter" type="button">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 01.628.74v2.288a2.25 2.25 0 01-.659 1.59l-4.682 4.683a2.25 2.25 0 00-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 018 18.25v-5.757a2.25 2.25 0 00-.659-1.591L2.659 6.22A2.25 2.25 0 012 4.629V2.34a.75.75 0 01.628-.74z" clip-rule="evenodd"/></svg>
                </button>
              </div>

              <div class="pb-tabs">
                <button class="pb-tab" :class="{ 'pb-tab--active': libraryTab === 'all' }" @click="libraryTab = 'all'">All</button>
                <button class="pb-tab" :class="{ 'pb-tab--active': libraryTab === 'in' }" @click="libraryTab = 'in'">In pathway</button>
                <button class="pb-tab" :class="{ 'pb-tab--active': libraryTab === 'out' }" @click="libraryTab = 'out'">Not in pathway</button>
              </div>

              <div v-if="loadingCourses" class="pb-pane__loading">
                <span class="pb-spinner"></span>
              </div>

              <div v-else-if="visibleLibraryCourses.length === 0" class="pb-pane__empty">
                No matching courses.
              </div>

              <ul v-else ref="libraryList" class="pb-lib-list">
                <li
                  v-for="course in visibleLibraryCourses"
                  :key="course.id"
                  class="pb-lib-item"
                  :data-course-id="course.id"
                  :class="{ 'pb-lib-item--used': isUsed(course.id) }">
                  <span class="pb-lib-item__icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M4 4a2 2 0 012-2h6.5a.5.5 0 01.354.146l3 3A.5.5 0 0116 5.5V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V6h-3a1 1 0 01-1-1V4H6z"/></svg>
                  </span>
                  <div class="pb-lib-item__body">
                    <div class="pb-lib-item__title">{{ course.name || course.title || ('Course #' + course.id) }}</div>
                    <div class="pb-lib-item__meta">
                      <span class="pb-lib-item__time">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="10" height="10" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0v5a.75.75 0 00.336.624l3 2a.75.75 0 00.832-1.248L10.75 10.348V5.75z" clip-rule="evenodd" /></svg>
                        {{ course.duration || 12 }} min
                      </span>
                      <span v-if="isUsed(course.id)" class="pb-lib-item__used">In pathway</span>
                    </div>
                  </div>
                  <button
                    class="pb-lib-item__add"
                    type="button"
                    :disabled="isUsed(course.id)"
                    :title="isUsed(course.id) ? 'Already in this pathway' : 'Add to pathway'"
                    @click="addCourse(course)">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd" /></svg>
                  </button>
                </li>
              </ul>
            </aside>

            <!-- ----- MIDDLE: Canvas ----- -->
            <main class="pb-pane pb-pane--canvas">
              <div class="pb-canvas__head">
                <div class="pb-canvas__head-left">
                  <h3 class="pb-pane__title">Pathway canvas</h3>
                  <span class="pb-canvas__summary">
                    <strong>{{ modules.length }}</strong> module{{ modules.length === 1 ? '' : 's' }}
                    <template v-if="modules.length > 0">
                      <span class="pb-canvas__summary-sep">·</span>
                      <strong>{{ gatedCount }}</strong> gated
                    </template>
                  </span>
                </div>
                <div class="pb-canvas__head-tools">
                  <div class="pb-segctrl">
                    <button class="pb-segctrl__btn pb-segctrl__btn--active" title="Card view" type="button">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path d="M3 4.5A1.5 1.5 0 014.5 3h11A1.5 1.5 0 0117 4.5v3A1.5 1.5 0 0115.5 9h-11A1.5 1.5 0 013 7.5v-3zm0 8A1.5 1.5 0 014.5 11h11a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 013 15.5v-3z"/></svg>
                    </button>
                    <button class="pb-segctrl__btn" title="List view" type="button">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 5A.75.75 0 012.75 9h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 9.75zm0 5a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" clip-rule="evenodd"/></svg>
                    </button>
                  </div>
                  <button class="pb-icon-btn" title="Find module" type="button">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/></svg>
                  </button>
                  <button class="pb-fit-btn" type="button">
                    Fit to view
                    <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clip-rule="evenodd"/></svg>
                  </button>
                  <button class="pb-icon-btn pb-icon-btn--danger" title="Clear all modules" type="button" @click="clearAllModules">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd"/></svg>
                  </button>
                </div>
              </div>

              <div class="pb-canvas__body">
                <ol ref="canvasList" class="pb-canvas-list">
                  <li
                    v-for="(mod, idx) in modules"
                    :key="'mod-' + (mod.id || mod._tempId)"
                    class="pb-mod-row"
                    :data-module-id="mod.id || mod._tempId">
                    <div
                      class="pb-mod"
                      :class="{ 'pb-mod--selected': isSelected(mod) }"
                      @click="selectModule(mod)">
                      <div class="pb-mod__drag" title="Drag to reorder" @click.stop>
                        <svg viewBox="0 0 20 20" width="13" height="13" fill="currentColor"><path d="M7 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6-10a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"/></svg>
                      </div>
                      <div class="pb-mod__icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M1 5.25A2.25 2.25 0 013.25 3h11.5A2.25 2.25 0 0117 5.25v9.5A2.25 2.25 0 0114.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm6.45.05a.75.75 0 00-1.2.6v8.2a.75.75 0 001.2.6l5.45-4.1a.75.75 0 000-1.2L7.45 5.3z" clip-rule="evenodd"/></svg>
                      </div>
                      <div class="pb-mod__main">
                        <div class="pb-mod__title">{{ mod.course_name || courseTitle(mod.course_id) }}</div>
                        <div class="pb-mod__meta">
                          <span class="pb-mod__time">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="10" height="10" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0v5a.75.75 0 00.336.624l3 2a.75.75 0 00.832-1.248L10.75 10.348V5.75z" clip-rule="evenodd" /></svg>
                            {{ moduleDuration(mod) }} min
                          </span>
                          <span class="pb-mod__chip" :class="'pb-mod__chip--' + chipKind(mod)">
                            {{ chipLabel(mod) }}
                          </span>
                        </div>
                      </div>
                      <select
                        v-model="mod.gating_rule"
                        class="pb-rule-select"
                        @click.stop
                        @change="onModuleChange(mod)">
                        <option value="optional">Always unlocked</option>
                        <option value="complete_previous">Unlock after previous</option>
                        <option value="complete_assessment">Unlock on date</option>
                      </select>
                      <button
                        class="pb-mod__remove"
                        type="button"
                        title="Remove module"
                        @click.stop="removeModule(mod, idx)">
                        <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd" /></svg>
                      </button>
                    </div>

                    <div class="pb-flow-sep">
                      <span class="pb-flow-line"></span>
                      <button
                        class="pb-flow-plus"
                        type="button"
                        title="Insert module here"
                        @click="focusLibrarySearch">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd" /></svg>
                      </button>
                      <span class="pb-flow-line"></span>
                    </div>
                  </li>
                </ol>

                <!-- Empty-state illustration (shown only when there are no modules) -->
                <div v-if="modules.length === 0" class="pb-empty-state">
                  <div class="pb-empty-state__art" aria-hidden="true">
                    <svg viewBox="0 0 220 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <!-- Browser window mock -->
                      <rect x="18" y="22" width="148" height="98" rx="7" fill="#FFFFFF" stroke="#D4D4D4" stroke-width="1.4"/>
                      <circle cx="28" cy="32" r="1.8" fill="#D4D4D4"/>
                      <circle cx="34" cy="32" r="1.8" fill="#D4D4D4"/>
                      <circle cx="40" cy="32" r="1.8" fill="#D4D4D4"/>
                      <line x1="18" y1="42" x2="166" y2="42" stroke="#EAEAEA" stroke-width="1"/>
                      <!-- Dashed add box -->
                      <rect x="32" y="56" width="50" height="48" rx="5" stroke="#BDBDBD" stroke-width="1.4" stroke-dasharray="3 3" fill="#FAFAFA"/>
                      <line x1="57" y1="72" x2="57" y2="88" stroke="#9CA3AF" stroke-width="1.6" stroke-linecap="round"/>
                      <line x1="49" y1="80" x2="65" y2="80" stroke="#9CA3AF" stroke-width="1.6" stroke-linecap="round"/>
                      <!-- Solid card with video icon and lines -->
                      <rect x="92" y="56" width="58" height="48" rx="5" fill="#FFFFFF" stroke="#D4D4D4" stroke-width="1.4"/>
                      <rect x="100" y="63" width="20" height="14" rx="2" fill="#1F2937"/>
                      <path d="M107 66.5L107 73.5L113 70Z" fill="#FFFFFF"/>
                      <line x1="100" y1="84" x2="142" y2="84" stroke="#EAEAEA" stroke-width="1.4" stroke-linecap="round"/>
                      <line x1="100" y1="91" x2="130" y2="91" stroke="#EAEAEA" stroke-width="1.4" stroke-linecap="round"/>
                      <!-- Sparkles -->
                      <path d="M174 26 L175.3 29.1 L178.4 30.4 L175.3 31.7 L174 34.8 L172.7 31.7 L169.6 30.4 L172.7 29.1 Z" fill="#9CA3AF"/>
                      <path d="M183 42 L183.7 43.7 L185.4 44.4 L183.7 45.1 L183 46.8 L182.3 45.1 L180.6 44.4 L182.3 43.7 Z" fill="#9CA3AF"/>
                      <!-- Cursor -->
                      <path d="M178 96 L178 120 L184 114 L188.5 124 L192 122 L187.5 112 L194.5 112 Z" fill="#1F2937" stroke="#FFFFFF" stroke-width="0.8" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div class="pb-empty-state__title">Your pathway is empty</div>
                  <div class="pb-empty-state__sub">Add modules from the course library to get started.</div>
                  <button class="pb-btn pb-btn--primary pb-empty-state__cta" type="button" @click="focusLibrarySearch">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" aria-hidden="true"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd"/></svg>
                    Add modules
                  </button>
                  <div class="pb-empty-state__link">
                    or <a class="pb-dropzone__link" @click.stop="focusLibrarySearch">browse course library</a>
                  </div>
                </div>

                <!-- Compact dropzone (between modules / appended) -->
                <div
                  v-else
                  class="pb-dropzone"
                  @click="focusLibrarySearch">
                  <div class="pb-dropzone__plus" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd" /></svg>
                  </div>
                  <div class="pb-dropzone__title">Add module</div>
                  <div class="pb-dropzone__sub">
                    Drag and drop courses here
                    <br>
                    or <a class="pb-dropzone__link" @click.stop="focusLibrarySearch">browse course library</a>
                  </div>
                </div>
              </div>

            </main>

            <!-- ----- RIGHT: Inspector ----- -->
            <aside class="pb-pane pb-pane--inspector">
              <div class="pb-pane__head pb-pane__head--insp">
                <h3 class="pb-pane__title">Module settings</h3>
                <button
                  v-if="selectedModule"
                  class="pb-insp-close"
                  type="button"
                  title="Close"
                  @click="selectedModuleKey = null">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M4.28 3.22a.75.75 0 00-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 101.06 1.06L10 11.06l5.72 5.72a.75.75 0 101.06-1.06L11.06 10l5.72-5.72a.75.75 0 00-1.06-1.06L10 8.94 4.28 3.22z" clip-rule="evenodd"/></svg>
                </button>
              </div>

              <div v-if="selectedModule" class="pb-insp">
                <div class="pb-insp__scroll">
                <div class="pb-insp__section">
                  <div class="pb-insp__section-title">Unlock rules</div>
                  <div class="pb-insp__section-sub">Define when this module becomes available to learners.</div>
                  <div class="pb-radio-group">
                    <label class="pb-radio">
                      <input
                        type="radio"
                        value="optional"
                        v-model="selectedModule.gating_rule"
                        @change="onModuleChange(selectedModule)" />
                      <span class="pb-radio__mark"></span>
                      <span class="pb-radio__body">
                        <span class="pb-radio__title">Always unlocked</span>
                        <span class="pb-radio__sub">Available immediately</span>
                      </span>
                    </label>
                    <label class="pb-radio">
                      <input
                        type="radio"
                        value="complete_previous"
                        v-model="selectedModule.gating_rule"
                        @change="onModuleChange(selectedModule)" />
                      <span class="pb-radio__mark"></span>
                      <span class="pb-radio__body">
                        <span class="pb-radio__title">Unlock after previous</span>
                        <span class="pb-radio__sub">Learners must complete the previous module</span>
                      </span>
                    </label>
                    <label class="pb-radio">
                      <input
                        type="radio"
                        value="complete_assessment"
                        v-model="selectedModule.gating_rule"
                        @change="onModuleChange(selectedModule)" />
                      <span class="pb-radio__mark"></span>
                      <span class="pb-radio__body">
                        <span class="pb-radio__title">Unlock on date</span>
                        <span class="pb-radio__sub">Set a specific date and time</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div class="pb-insp__section">
                  <div class="pb-insp__section-title">Module options</div>
                  <div class="pb-check-group">
                    <!-- TODO(glad): backend column for show_in_pathway -->
                    <label class="pb-check">
                      <input type="checkbox" v-model="selectedModule.show_in_pathway" @change="markDirty" />
                      <span class="pb-check__mark"></span>
                      <span class="pb-check__body">
                        <span class="pb-check__title">Show in learner pathway</span>
                        <span class="pb-check__sub">Visible to learners on their pathway</span>
                      </span>
                    </label>
                    <!-- TODO(glad): backend column for require_completion -->
                    <label class="pb-check">
                      <input type="checkbox" v-model="selectedModule.require_completion" @change="markDirty" />
                      <span class="pb-check__mark"></span>
                      <span class="pb-check__body">
                        <span class="pb-check__title">Require completion</span>
                        <span class="pb-check__sub">Learners must complete to unlock next module</span>
                      </span>
                    </label>
                    <!-- TODO(glad): backend column for allow_skipping -->
                    <label class="pb-check">
                      <input type="checkbox" v-model="selectedModule.allow_skipping" @change="markDirty" />
                      <span class="pb-check__mark"></span>
                      <span class="pb-check__body">
                        <span class="pb-check__title">Allow skipping</span>
                        <span class="pb-check__sub">Learners can skip this module</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div class="pb-insp__section">
                  <label class="pb-field-label">
                    Description
                    <span class="pb-field-label__opt">(optional)</span>
                  </label>
                  <!-- TODO(glad): backend column for per-module description -->
                  <textarea
                    class="pb-textarea"
                    v-model="selectedModule.description"
                    placeholder="Add notes or learning objectives for this module…"
                    rows="3"
                    @input="markDirty"></textarea>
                </div>
                </div><!-- /.pb-insp__scroll -->

                <div class="pb-insp__footer">
                <button class="pb-btn pb-btn--destructive pb-btn--block" type="button" @click="removeSelected">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd" /></svg>
                  Remove from pathway
                </button>
                </div><!-- /.pb-insp__footer -->
              </div>

              <div v-else class="pb-insp-empty">
                <div class="pb-insp-empty__title">Select a module</div>
                <div class="pb-insp-empty__sub">Choose a module from the canvas to view and edit its settings.</div>
                <div class="pb-insp-empty__icon-card" aria-hidden="true">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="21" y1="6" x2="14" y2="6"/>
                    <line x1="10" y1="6" x2="3" y2="6"/>
                    <line x1="21" y1="12" x2="12" y2="12"/>
                    <line x1="8" y1="12" x2="3" y2="12"/>
                    <line x1="21" y1="18" x2="16" y2="18"/>
                    <line x1="12" y1="18" x2="3" y2="18"/>
                    <line x1="14" y1="4" x2="14" y2="8"/>
                    <line x1="8" y1="10" x2="8" y2="14"/>
                    <line x1="16" y1="16" x2="16" y2="20"/>
                  </svg>
                </div>
              </div>
            </aside>
          </div>

          <!-- Toast -->
          <div v-if="toast" class="pb-toast" :class="'pb-toast--' + toast.kind">
            {{ toast.msg }}
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import { getGladTemplate } from '@/utils/gladPathwayTemplates';

// Click-outside directive (locally registered) — closes the kebab menu.
const clickOutside = {
  bind(el, binding) {
    el.__pbClickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener('click', el.__pbClickOutside);
  },
  unbind(el) {
    document.removeEventListener('click', el.__pbClickOutside);
    delete el.__pbClickOutside;
  },
};

export default {
  name: 'PathwayBuilder',
  directives: { clickOutside },
  data() {
    return {
      pathway: {
        id: null,
        title: '',
        subtitle: '',
        description: '',
        thumbnail: '',
        track: '',
        status: 'draft',
      },
      modules: [],
      courses: [],
      loadingCourses: false,
      librarySearch: '',
      libraryTab: 'all',
      saving: false,
      dirty: false,
      savedAt: null,
      toast: null,
      sortableLibrary: null,
      sortableCanvas: null,
      tempIdCounter: 1,
      selectedModuleKey: null,
      menuOpen: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    showMetaRow() {
      return this.isEdit || !!this.pathway.track || this.modules.length > 0;
    },
    canPublish() {
      return this.pathway.title && this.pathway.title.trim().length > 0 && this.modules.length > 0;
    },
    gatedCount() {
      return this.modules.filter(m => m.gating_rule && m.gating_rule !== 'optional').length;
    },
    totalDuration() {
      if (this.modules.length === 0) return 0;
      return this.modules.reduce((sum, m) => sum + this.moduleDuration(m), 0);
    },
    filteredCourses() {
      const q = (this.librarySearch || '').trim().toLowerCase();
      if (!q) return this.courses;
      return this.courses.filter(c => {
        const n = (c.name || c.title || '').toLowerCase();
        return n.indexOf(q) !== -1;
      });
    },
    visibleLibraryCourses() {
      let list = this.filteredCourses;
      if (this.libraryTab === 'in') {
        list = list.filter(c => this.isUsed(c.id));
      } else if (this.libraryTab === 'out') {
        list = list.filter(c => !this.isUsed(c.id));
      }
      return list;
    },
    selectedModule() {
      if (this.selectedModuleKey == null) return null;
      return this.modules.find(m => (m.id || m._tempId) === this.selectedModuleKey) || null;
    },
    selectedIndex() {
      if (!this.selectedModule) return -1;
      return this.modules.indexOf(this.selectedModule);
    },
  },
  created() {
    this.fetchCourses();
    if (this.isEdit) {
      this.fetchPathway(this.$route.params.id);
    } else {
      this.applyTemplateFromQuery();
    }
  },
  mounted() {
    this.$nextTick(() => this.setupSortables());
  },
  beforeDestroy() {
    if (this.sortableLibrary) this.sortableLibrary.destroy();
    if (this.sortableCanvas) this.sortableCanvas.destroy();
  },
  watch: {
    'modules.length'() {
      this.$nextTick(() => this.setupCanvasSortable());
    },
    visibleLibraryCourses() {
      this.$nextTick(() => this.setupLibrarySortable());
    },
  },
  methods: {
    // ---- Sortable setup ----
    setupSortables() {
      this.setupLibrarySortable();
      this.setupCanvasSortable();
    },
    setupLibrarySortable() {
      const el = this.$refs.libraryList;
      if (!el) return;
      if (this.sortableLibrary) {
        this.sortableLibrary.destroy();
        this.sortableLibrary = null;
      }
      this.sortableLibrary = Sortable.create(el, {
        group: { name: 'pathway-modules', pull: 'clone', put: false },
        sort: false,
        animation: 180,
        ghostClass: 'pb-sortable-ghost',
        chosenClass: 'pb-sortable-chosen',
        dragClass: 'pb-sortable-drag',
        onEnd: (evt) => {
          const toEl = evt.to;
          const fromEl = evt.from;
          if (toEl === fromEl) return;
          const courseId = parseInt(evt.item.getAttribute('data-course-id'), 10);
          if (evt.item && evt.item.parentNode) {
            evt.item.parentNode.removeChild(evt.item);
          }
          if (!courseId) return;
          if (this.isUsed(courseId)) {
            this.flash('Course already in pathway', 'warn');
            return;
          }
          const course = this.courses.find(c => c.id === courseId);
          if (course) {
            this.addCourseAt(course, evt.newIndex);
          }
        },
      });
    },
    setupCanvasSortable() {
      const el = this.$refs.canvasList;
      if (!el) return;
      if (this.sortableCanvas) {
        this.sortableCanvas.destroy();
        this.sortableCanvas = null;
      }
      this.sortableCanvas = Sortable.create(el, {
        group: { name: 'pathway-modules', put: true },
        animation: 200,
        handle: '.pb-mod__drag',
        ghostClass: 'pb-sortable-ghost',
        chosenClass: 'pb-sortable-chosen',
        dragClass: 'pb-sortable-drag',
        onUpdate: (evt) => {
          const moved = this.modules.splice(evt.oldIndex, 1)[0];
          this.modules.splice(evt.newIndex, 0, moved);
          this.modules.forEach((m, i) => { m.sequence_order = i + 1; });
          this.markDirty();
        },
      });
    },

    // ---- Data fetching ----
    fetchCourses() {
      this.loadingCourses = true;
      this.$http.post('course/all_courses', {
        page: 1,
        per_page: 200,
        company_id: '',
        search: '',
        course_status: '',
      })
        .then(resp => {
          const raw = Array.isArray(resp.data) ? resp.data : (resp.data.courses || []);
          this.courses = raw;
        })
        .catch(() => { this.courses = []; })
        .finally(() => {
          this.loadingCourses = false;
          this.$nextTick(() => this.setupLibrarySortable());
        });
    },
    fetchPathway(id) {
      this.$http.get('glad/pathways/' + id)
        .then(resp => {
          const p = resp.data || {};
          this.pathway = {
            id: p.id,
            title: p.title || '',
            subtitle: p.subtitle || '',
            description: p.description || '',
            thumbnail: p.thumbnail || '',
            track: p.track || '',
            status: p.status || 'draft',
          };
          this.modules = (p.modules || []).map(m => ({
            id: m.id,
            course_id: m.course_id,
            course_name: m.course_name,
            sequence_order: m.sequence_order,
            gating_rule: m.gating_rule || 'complete_previous',
            // Session-local inspector fields — not yet backed by the API.
            show_in_pathway: true,
            require_completion: m.gating_rule === 'complete_previous',
            allow_skipping: false,
            description: '',
          }));
          this.dirty = false;
          this.$nextTick(() => this.setupCanvasSortable());
        })
        .catch(() => { this.flash('Could not load pathway', 'error'); });
    },
    applyTemplateFromQuery() {
      const id = this.$route.query && this.$route.query.template;
      if (!id) return;
      const tpl = getGladTemplate(id);
      if (!tpl || tpl.id === 'blank') return;
      this.pathway.title = tpl.title || '';
      this.pathway.subtitle = tpl.subtitle || '';
      this.pathway.track = tpl.track || '';
      this.dirty = true;
      this.flash('Template applied — review and save', 'ok');
    },

    // ---- Module helpers ----
    isUsed(courseId) {
      return this.modules.some(m => m.course_id === courseId);
    },
    courseTitle(courseId) {
      const c = this.courses.find(c => c.id === courseId);
      return c ? (c.name || c.title || ('Course #' + courseId)) : ('Course #' + courseId);
    },
    moduleDuration(mod) {
      const c = this.courses.find(c => c.id === mod.course_id);
      return (c && c.duration) || 12;
    },
    chipKind(mod) {
      if (mod.gating_rule === 'optional') return 'optional';
      if (mod.gating_rule === 'complete_assessment') return 'date';
      return 'sequential';
    },
    chipLabel(mod) {
      if (mod.gating_rule === 'optional') return 'Optional';
      if (mod.gating_rule === 'complete_assessment') return 'On date';
      return 'Sequential';
    },
    statusLabel(s) {
      if (s === 'published') return 'Published';
      if (s === 'archived') return 'Archived';
      return 'Draft';
    },

    // ---- Module mutations ----
    addCourse(course) {
      if (this.isUsed(course.id)) {
        this.flash('Course already in pathway', 'warn');
        return;
      }
      const mod = this.makeModule(course);
      this.modules.push(mod);
      this.selectedModuleKey = mod._tempId;
      this.markDirty();
    },
    addCourseAt(course, index) {
      if (this.isUsed(course.id)) return;
      const mod = this.makeModule(course);
      const at = typeof index === 'number' ? index : this.modules.length;
      this.modules.splice(at, 0, mod);
      this.modules.forEach((m, i) => { m.sequence_order = i + 1; });
      this.selectedModuleKey = mod._tempId;
      this.markDirty();
    },
    makeModule(course) {
      return {
        _tempId: 't' + (this.tempIdCounter++),
        course_id: course.id,
        course_name: course.name || course.title,
        sequence_order: this.modules.length + 1,
        gating_rule: 'complete_previous',
        // Session-local inspector fields.
        show_in_pathway: true,
        require_completion: true,
        allow_skipping: false,
        description: '',
      };
    },
    removeModule(mod, idx) {
      const wasSelected = this.isSelected(mod);
      if (mod.id && this.pathway.id) {
        this.$http.delete('glad/pathways/' + this.pathway.id + '/modules/' + mod.id)
          .then(() => {
            this.modules.splice(idx, 1);
            this.modules.forEach((m, i) => { m.sequence_order = i + 1; });
            if (wasSelected) this.selectedModuleKey = null;
            this.flash('Module removed', 'ok');
          })
          .catch(() => { this.flash('Could not remove module', 'error'); });
      } else {
        this.modules.splice(idx, 1);
        this.modules.forEach((m, i) => { m.sequence_order = i + 1; });
        if (wasSelected) this.selectedModuleKey = null;
        this.markDirty();
      }
    },
    removeSelected() {
      const m = this.selectedModule;
      if (!m) return;
      const idx = this.modules.indexOf(m);
      if (idx < 0) return;
      this.removeModule(m, idx);
    },
    clearAllModules() {
      if (this.modules.length === 0) return;
      if (!window.confirm('Remove all modules from this pathway?')) return;
      // Remove from end to start so indexes stay stable.
      for (let i = this.modules.length - 1; i >= 0; i--) {
        this.removeModule(this.modules[i], i);
      }
      this.selectedModuleKey = null;
    },
    onModuleChange(mod) {
      this.markDirty();
    },

    // ---- Selection ----
    isSelected(mod) {
      return this.selectedModuleKey === (mod.id || mod._tempId);
    },
    selectModule(mod) {
      this.selectedModuleKey = mod.id || mod._tempId;
    },

    // ---- Inspector helpers (stubs) ----
    changeModuleCourse() {
      this.flash('Module-swap picker coming soon', 'warn');
    },

    // ---- Header actions ----
    focusLibrarySearch() {
      const el = this.$refs.librarySearchInput;
      if (el) el.focus();
    },
    openPreview() {
      if (this.pathway.id) {
        this.$router.push('/glad/pathways/' + this.pathway.id);
      } else {
        this.flash('Save the pathway first to preview', 'warn');
      }
    },
    onMenuPublish() {
      this.menuOpen = false;
      if (!this.canPublish) {
        this.flash('Add a title and at least one module first', 'warn');
        return;
      }
      this.publish();
    },
    onMenuDuplicate() {
      this.menuOpen = false;
      this.flash('Duplicate — coming soon', 'warn');
    },
    onMenuArchive() {
      this.menuOpen = false;
      this.flash('Archive — coming soon', 'warn');
    },
    closeMenu() {
      this.menuOpen = false;
    },

    // ---- State helpers ----
    markDirty() {
      this.dirty = true;
      this.savedAt = null;
    },

    // ---- Persistence ----
    async saveDraft() {
      await this.persist({ keepStatus: true });
    },
    async publish() {
      await this.persist({ keepStatus: false, publish: true });
    },
    async persist({ keepStatus, publish }) {
      if (this.saving) return;
      if (!this.pathway.title || !this.pathway.title.trim()) {
        this.flash('Title is required', 'warn');
        return;
      }
      this.saving = true;

      try {
        const payload = {
          title: this.pathway.title,
          subtitle: this.pathway.subtitle || null,
          description: this.pathway.description || null,
          thumbnail: this.pathway.thumbnail || null,
          track: this.pathway.track || null,
        };
        if (publish) {
          payload.status = 'published';
        } else if (keepStatus && this.pathway.status === 'draft') {
          payload.status = 'draft';
        }

        let pathwayId = this.pathway.id;
        if (pathwayId) {
          const r = await this.$http.put('glad/pathways/' + pathwayId, payload);
          this.pathway.status = r.data.status || this.pathway.status;
        } else {
          const r = await this.$http.post('glad/pathways', { ...payload, status: payload.status || 'draft' });
          pathwayId = r.data.id;
          this.pathway.id = pathwayId;
          this.pathway.status = r.data.status || 'draft';
        }

        for (let i = 0; i < this.modules.length; i++) {
          const m = this.modules[i];
          if (!m.id) {
            const r = await this.$http.post('glad/pathways/' + pathwayId + '/modules', {
              course_id: m.course_id,
              sequence_order: i + 1,
              gating_rule: m.gating_rule,
            });
            m.id = r.data.id;
            m.course_name = m.course_name || (r.data.course && r.data.course.name) || this.courseTitle(m.course_id);
            // Preserve selection across the temp-id → real-id swap.
            if (this.selectedModuleKey === m._tempId) {
              this.selectedModuleKey = m.id;
            }
            delete m._tempId;
          } else {
            await this.$http.put('glad/pathways/' + pathwayId + '/modules/' + m.id, {
              gating_rule: m.gating_rule,
            });
          }
        }

        if (this.modules.length > 0) {
          await this.$http.post('glad/pathways/' + pathwayId + '/modules/reorder', {
            order: this.modules.map((m, i) => ({ id: m.id, sequence_order: i + 1 })),
          });
        }

        if (publish) {
          this.pathway.status = 'published';
        }
        this.dirty = false;
        this.savedAt = Date.now();
        this.flash(publish ? 'Pathway published — visible to educators' : 'Draft saved', 'ok');

        if (!this.$route.params.id && this.pathway.id) {
          this.$router.replace('/glad/pathways/' + this.pathway.id + '/edit');
        }
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) || 'Save failed';
        this.flash(msg, 'error');
      } finally {
        this.saving = false;
      }
    },

    flash(msg, kind) {
      this.toast = { msg, kind: kind || 'ok' };
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.toast = null; }, 2400);
    },
    goBack() {
      this.$router.push('/glad/pathways');
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

/* ============================================================
   PathwayBuilder — shadcn-inspired three-pane workspace
   surface  → card  (#ffffff)
   muted    → paper (#fafaf7)
   secondary→ paper-2 (#f3f2ee)
   fg       → ink
   muted-fg → text-soft
   border   → line
   ring     → ink @ 15%
   radius   → 6 / 8 / 10
   ============================================================ */

.pathway-builder-wrap { width: 100%; }
.pathway-builder-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
  background: transparent;
}

.pb {
  /* Local CSS-var fallbacks — scoped :root rewrites lose these otherwise */
  --glad-ink:         #{$glad-ink};
  --glad-ink-2:       #{$glad-ink-2};
  --glad-paper:       #{$glad-paper};
  --glad-paper-2:     #{$glad-paper-2};
  --glad-surface:     #{$glad-surface};
  --glad-line:        #{$glad-line};
  --glad-line-strong: #{$glad-line-strong};
  --glad-text:        #{$glad-text};
  --glad-text-soft:   #{$glad-text-soft};
  --glad-text-muted:  #{$glad-text-muted};
  --glad-accent:      #{$glad-accent};
  --glad-success:     #{$glad-success};
  --glad-danger:      #{$glad-danger};
  --glad-font-sans:   #{$glad-font-sans};
  --glad-font-serif:  #{$glad-font-serif};

  font-family: var(--glad-font-sans);
  color: var(--glad-text);
  font-feature-settings: 'cv11', 'ss01';
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  min-height: 600px;
  max-height: 1100px;
}

/* ---------- TOP HEADER ---------- */
.pb-top {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1.5rem;
  align-items: end;
  padding: 1.1rem 1.25rem 1.1rem;
  border-bottom: 1px solid var(--glad-line);
  background: var(--glad-surface);
  flex-shrink: 0;
}
@media (max-width: 1080px) {
  .pb-top {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

.pb-top__left { min-width: 0; display: flex; flex-direction: column; gap: 0.35rem; }

.pb-crumbs {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: var(--glad-text-soft);
}
.pb-crumb {
  color: var(--glad-text-soft);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.12s ease;
  &:hover { color: var(--glad-ink); }
}
.pb-crumb--current { color: var(--glad-ink); font-weight: 500; cursor: default; }
.pb-crumb-sep { color: var(--glad-line-strong); }

.pb-title-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}
.pb-title-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  outline: none;
  padding: 0;
  font-family: var(--glad-font-serif);
  font-size: 1.625rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.15;
  color: var(--glad-ink);
  &::placeholder { color: var(--glad-text-muted); opacity: 1; font-weight: 500; }
  &:focus {
    box-shadow: inset 0 -2px 0 0 var(--glad-line);
  }
}
.pb-status {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
  &--draft     { background: var(--glad-paper-2); color: var(--glad-text-soft); }
  &--published { background: #dcfce7; color: #166534; }
  &--archived  { background: #fee2e2; color: #991b1b; }
}
.pb-subtitle-input {
  border: 0;
  background: transparent;
  outline: none;
  padding: 0;
  font-family: var(--glad-font-sans);
  font-size: 0.875rem;
  color: var(--glad-text-soft);
  &::placeholder { color: var(--glad-text-muted); opacity: 1; }
  &:focus { color: var(--glad-text); }
}

.pb-meta-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.45rem;
}
.pb-meta-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.pb-meta-select {
  appearance: none;
  -webkit-appearance: none;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  padding: 0.32rem 1.5rem 0.32rem 0.65rem;
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--glad-ink);
  cursor: pointer;
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &:hover { border-color: var(--glad-line-strong); }
  &:focus {
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.08);
  }
}
.pb-meta-select__chev {
  position: absolute;
  right: 0.5rem;
  pointer-events: none;
  color: var(--glad-text-soft);
}
.pb-meta-status {
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--glad-text-soft);
  padding: 0.32rem 0.2rem;
}

.pb-top__stats {
  display: flex;
  gap: 1.25rem;
  align-items: flex-end;
  align-self: flex-end;
}
.pb-stat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 60px;
}
.pb-stat__value {
  font-family: var(--glad-font-sans);
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--glad-ink);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.pb-stat__unit {
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--glad-text-muted);
  margin-left: 0.2rem;
}
.pb-stat__value--muted { color: var(--glad-text-muted); font-weight: 500; }
.pb-stat__label {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--glad-text-soft);
  margin-top: 0.15rem;
}

.pb-top__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-end;
}
.pb-saved {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  margin-right: 0.25rem;
}
.pb-saved--ok    { color: var(--glad-success); }
.pb-saved--dirty { color: var(--glad-accent); }

.pb-kebab-wrap { position: relative; }
.pb-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 17, 21, 0.12);
  padding: 0.25rem;
  min-width: 180px;
  z-index: 100;
}
.pb-menu__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  font-family: var(--glad-font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-ink);
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover:not(:disabled) { background: var(--glad-paper); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  svg { color: var(--glad-text-soft); }
  &.pb-menu__item--danger {
    color: var(--glad-danger);
    svg { color: var(--glad-danger); }
    &:hover:not(:disabled) { background: #fef2f2; }
  }
}
.pb-menu__sep {
  height: 1px;
  background: var(--glad-line);
  margin: 0.25rem 0.4rem;
}

/* ---------- BODY (3-column grid) ---------- */
.pb-body {
  display: grid;
  grid-template-columns: 300px 1fr 360px;
  background: var(--glad-paper);
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
@media (max-width: 1280px) {
  .pb-body {
    grid-template-columns: 280px 1fr 340px;
  }
}
@media (max-width: 1100px) {
  .pb-body {
    grid-template-columns: 260px 1fr 320px;
  }
}

.pb-pane {
  display: flex;
  flex-direction: column;
  background: var(--glad-surface);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}
.pb-pane--library { border-right: 1px solid var(--glad-line); }
.pb-pane--inspector {
  border-left: 1px solid var(--glad-line);
  background: var(--glad-paper);
}
.pb-pane--canvas {
  background: var(--glad-paper);
}

.pb-pane__head {
  padding: 0.875rem 1rem 0.65rem;
  border-bottom: 1px solid transparent;
}
.pb-pane__head--insp {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.pb-insp-close {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 0;
  background: transparent;
  color: var(--glad-text-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  &:hover { background: var(--glad-paper-2); color: var(--glad-ink); }
}
.pb-pane__title {
  font-family: var(--glad-font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin: 0;
  letter-spacing: -0.005em;
}
.pb-pane__sub {
  margin: 0.15rem 0 0;
  font-size: 0.75rem;
  color: var(--glad-text-soft);
}
.pb-pane__loading,
.pb-pane__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  color: var(--glad-text-soft);
  font-size: 0.83rem;
}

.pb-spinner {
  width: 22px; height: 22px;
  border: 2px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: pb-spin 0.7s linear infinite;
}
@keyframes pb-spin { to { transform: rotate(360deg); } }

/* ---------- LIBRARY ---------- */
.pb-search-row {
  display: flex;
  gap: 0.4rem;
  padding: 0 1rem 0.625rem;
}
.pb-search {
  position: relative;
  flex: 1;
  min-width: 0;
}
.pb-search__icon {
  position: absolute;
  top: 50%;
  left: 0.625rem;
  transform: translateY(-50%);
  color: var(--glad-text-muted);
}
.pb-search__input {
  width: 100%;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  padding: 0.45rem 0.65rem 0.45rem 1.9rem;
  font-family: var(--glad-font-sans);
  font-size: 0.83rem;
  color: var(--glad-ink);
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &::placeholder { color: var(--glad-text-muted); opacity: 1; }
  &:focus {
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.08);
  }
}
.pb-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--glad-line);
  background: var(--glad-surface);
  color: var(--glad-text-soft);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  &:hover:not(:disabled) {
    background: var(--glad-paper);
    color: var(--glad-ink);
    border-color: var(--glad-line-strong);
  }
  &--sm { width: 28px; height: 28px; }
  &--danger:hover { background: #fef2f2; color: var(--glad-danger); border-color: #fecaca; }
}

.pb-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0 1rem;
  border-bottom: 1px solid var(--glad-line);
}
.pb-tab {
  background: transparent;
  border: 0;
  padding: 0.5rem 0;
  margin-right: 0.5rem;
  font-family: var(--glad-font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-text-soft);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.12s ease, border-color 0.12s ease;
  &:hover { color: var(--glad-ink); }
  &--active {
    color: var(--glad-ink);
    border-bottom-color: var(--glad-ink);
  }
}

.pb-lib-list {
  list-style: none;
  margin: 0;
  padding: 0.5rem 0.5rem 1rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.pb-lib-item {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.55rem;
  border-radius: 6px;
  cursor: grab;
  transition: background 0.12s ease;
  &:hover { background: var(--glad-paper); }
  &:active { cursor: grabbing; }
  &--used { opacity: 0.6; cursor: not-allowed; }
}
.pb-lib-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 5px;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
}
.pb-lib-item__body { min-width: 0; }
.pb-lib-item__title {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.35;
}
.pb-lib-item__meta {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.7rem;
  color: var(--glad-text-muted);
  margin-top: 0.1rem;
}
.pb-lib-item__time { display: inline-flex; align-items: center; gap: 0.2rem; }
.pb-lib-item__used {
  background: #dcfce7;
  color: #166534;
  font-size: 0.66rem;
  font-weight: 500;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
}
.pb-lib-item__add {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--glad-text-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  &:hover:not(:disabled) {
    background: var(--glad-ink);
    color: #fff;
    border-color: var(--glad-ink);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

/* ---------- CANVAS ---------- */
.pb-canvas__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--glad-line);
  background: var(--glad-surface);
  gap: 0.65rem;
  flex-wrap: nowrap;
}
.pb-canvas__head-left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  flex: 1 1 auto;
}
.pb-canvas__head-left .pb-pane__title { white-space: nowrap; }
.pb-canvas__summary {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--glad-paper-2);
  border-radius: 999px;
  padding: 0.18rem 0.625rem;
  font-size: 0.72rem;
  color: var(--glad-text-soft);
  strong { color: var(--glad-ink); font-weight: 600; }
}
.pb-canvas__summary-sep { color: var(--glad-line-strong); }

.pb-canvas__head-tools {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: nowrap;
  flex-shrink: 0;
}

.pb-segctrl {
  display: inline-flex;
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  overflow: hidden;
  background: var(--glad-surface);
}
.pb-segctrl__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  color: var(--glad-text-soft);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  & + & { border-left: 1px solid var(--glad-line); }
  &:hover { color: var(--glad-ink); background: var(--glad-paper); }
  &--active { background: var(--glad-paper-2); color: var(--glad-ink); }
}
.pb-fit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 32px;
  padding: 0 0.7rem;
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  background: var(--glad-surface);
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--glad-ink);
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
  &:hover { background: var(--glad-paper); border-color: var(--glad-line-strong); }
  svg { color: var(--glad-text-soft); }
}

.pb-canvas__body {
  flex: 1 1 0;
  overflow-y: auto;
  padding: 1.25rem 1.5rem 1.5rem;
  background: var(--glad-paper);
  min-height: 0;
}
.pb-canvas__head { flex-shrink: 0; }
.pb-canvas-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 720px;
  margin-inline: auto;
}
.pb-mod-row {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pb-mod {
  width: 100%;
  display: grid;
  grid-template-columns: 18px 28px 1fr minmax(180px, auto) auto;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.85rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  &:hover { border-color: var(--glad-line-strong); }
  &--selected {
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.07);
  }
}
@media (max-width: 720px) {
  .pb-mod { grid-template-columns: 18px 28px 1fr auto; }
  .pb-rule-select { grid-column: 2 / -1; margin-top: 0.4rem; }
}
.pb-mod__drag {
  color: var(--glad-text-muted);
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  &:active { cursor: grabbing; }
  &:hover { color: var(--glad-text-soft); }
}
.pb-mod__order {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--glad-font-sans);
  font-weight: 600;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}
.pb-mod__icon {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pb-mod__main { min-width: 0; }
.pb-mod__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--glad-ink);
  font-family: var(--glad-font-sans);
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.35;
  margin-bottom: 0.2rem;
}
.pb-mod__meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.pb-mod__time {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
  font-size: 0.72rem;
  color: var(--glad-text-soft);
}
.pb-mod__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
  font-size: 0.68rem;
  font-weight: 500;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  &--optional   { background: rgba(180, 83, 9, 0.1); color: #92400e; }
  &--sequential { background: var(--glad-paper-2);   color: var(--glad-text-soft); }
  &--date       { background: rgba(59, 130, 246, 0.08); color: #1e3a8a; }
}
.pb-rule-select {
  height: 30px;
  min-width: 170px;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 7px;
  padding: 0 0.55rem;
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  color: var(--glad-ink);
  cursor: pointer;
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &:hover { border-color: var(--glad-line-strong); }
  &:focus {
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.08);
  }
}
.pb-mod__remove {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  color: var(--glad-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  &:hover { background: #fef2f2; color: var(--glad-danger); }
}

.pb-flow-sep {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0.15rem 0;
}
.pb-flow-line {
  width: 1px;
  height: 16px;
  background: var(--glad-line-strong);
}
.pb-flow-plus {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid var(--glad-line-strong);
  background: var(--glad-surface);
  color: var(--glad-text-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease, transform 0.12s ease;
  &:hover {
    background: var(--glad-ink);
    color: #fff;
    border-color: var(--glad-ink);
    transform: scale(1.04);
  }
  svg { width: 16px; height: 16px; }
}

.pb-dropzone {
  margin: 0.5rem auto 0;
  max-width: 720px;
  border: 1.5px dashed var(--glad-line-strong);
  border-radius: 10px;
  padding: 1.4rem 1rem;
  text-align: center;
  background: var(--glad-surface);
  color: var(--glad-text-soft);
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
  &:hover {
    border-color: var(--glad-ink);
    background: var(--glad-paper);
  }
  &--empty {
    padding: 3.5rem 1.5rem;
  }
}
.pb-dropzone__plus {
  width: 32px;
  height: 32px;
  margin: 0 auto 0.5rem;
  border-radius: 999px;
  background: var(--glad-paper-2);
  color: var(--glad-ink);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pb-dropzone__title {
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--glad-ink);
  letter-spacing: -0.005em;
}
.pb-dropzone__sub {
  font-size: 0.78rem;
  margin-top: 0.2rem;
  color: var(--glad-text-soft);
  line-height: 1.5;
}
.pb-dropzone__link {
  color: var(--glad-ink);
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover { color: var(--glad-accent); }
}

.pb-canvas__foot {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-top: 1px solid var(--glad-line);
  background: var(--glad-surface);
}
.pb-zoom-pct {
  font-family: var(--glad-font-sans);
  font-size: 0.74rem;
  color: var(--glad-text-soft);
  padding: 0 0.4rem;
  font-variant-numeric: tabular-nums;
}

/* ---------- INSPECTOR ---------- */
.pb-insp {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.pb-insp__scroll {
  flex: 1 1 0;
  overflow-y: auto;
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}
.pb-insp__footer {
  flex-shrink: 0;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid var(--glad-line);
  background: var(--glad-paper);
}
.pb-pane--inspector .pb-pane__head { flex-shrink: 0; }
.pb-pane--library .pb-pane__head,
.pb-pane--library .pb-search-row,
.pb-pane--library .pb-tabs { flex-shrink: 0; }
.pb-insp__heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.65rem 0.7rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 10px;
}
.pb-insp__num {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: var(--glad-paper-2);
  color: var(--glad-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--glad-font-sans);
  font-weight: 600;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
}
.pb-insp__name {
  font-family: var(--glad-font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--glad-ink);
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pb-insp__row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.pb-insp__row-label {
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  font-size: 0.8125rem;
}
.pb-insp__label-muted { color: var(--glad-text-soft); }
.pb-insp__label-value { color: var(--glad-ink); font-weight: 500; }

.pb-insp__section {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding-top: 0.25rem;
  flex-shrink: 0;
}
.pb-insp__section-title {
  font-family: var(--glad-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--glad-ink);
  letter-spacing: -0.005em;
}
.pb-insp__section-sub {
  font-size: 0.74rem;
  color: var(--glad-text-soft);
  margin-top: -0.35rem;
}
.pb-insp__hint {
  font-size: 0.78rem;
  color: var(--glad-text-soft);
  margin: 0;
}

/* Radios */
.pb-radio-group { display: flex; flex-direction: column; gap: 0.25rem; }
.pb-radio {
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover { background: var(--glad-surface); }
  input { position: absolute; opacity: 0; pointer-events: none; }
}
.pb-radio { flex-shrink: 0; }
.pb-radio__mark {
  width: 16px;
  height: 16px;
  border: 1.5px solid var(--glad-line-strong);
  border-radius: 999px;
  background: var(--glad-surface);
  position: relative;
  flex-shrink: 0;
  margin-top: 1px;
  transition: border-color 0.12s ease;
  &::after {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    width: 8px; height: 8px;
    border-radius: 999px;
    background: var(--glad-ink);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.12s ease;
  }
}
.pb-radio input:checked + .pb-radio__mark {
  border-color: var(--glad-ink);
  &::after { transform: translate(-50%, -50%) scale(1); }
}
.pb-radio input:focus-visible + .pb-radio__mark {
  box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.15);
}
.pb-radio__body { display: flex; flex-direction: column; gap: 0.05rem; min-width: 0; }
.pb-radio__title {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-ink);
  line-height: 1.25;
}
.pb-radio__sub {
  font-size: 0.72rem;
  color: var(--glad-text-soft);
  line-height: 1.35;
}

/* Checkboxes */
.pb-check-group { display: flex; flex-direction: column; gap: 0.25rem; }
.pb-check {
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover { background: var(--glad-surface); }
  input { position: absolute; opacity: 0; pointer-events: none; }
}
.pb-check { flex-shrink: 0; }
.pb-check__mark {
  width: 16px;
  height: 16px;
  border: 1.5px solid var(--glad-line-strong);
  border-radius: 4px;
  background: var(--glad-surface);
  position: relative;
  flex-shrink: 0;
  margin-top: 1px;
  transition: background 0.12s ease, border-color 0.12s ease;
  &::after {
    content: '';
    position: absolute;
    top: 1px; left: 4px;
    width: 5px; height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) scale(0);
    transform-origin: center;
    transition: transform 0.12s ease;
  }
}
.pb-check input:checked + .pb-check__mark {
  background: var(--glad-ink);
  border-color: var(--glad-ink);
  &::after { transform: rotate(45deg) scale(1); }
}
.pb-check input:focus-visible + .pb-check__mark {
  box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.15);
}
.pb-check__body { display: flex; flex-direction: column; gap: 0.05rem; min-width: 0; }
.pb-check__title {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-ink);
  line-height: 1.25;
}
.pb-check__sub {
  font-size: 0.72rem;
  color: var(--glad-text-soft);
  line-height: 1.35;
}

/* Field label + textarea */
.pb-field-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-ink);
}
.pb-field-label__opt {
  color: var(--glad-text-muted);
  font-weight: 400;
  margin-left: 0.2rem;
}
.pb-textarea {
  width: 100%;
  min-height: 84px;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  font-family: var(--glad-font-sans);
  font-size: 0.83rem;
  color: var(--glad-ink);
  resize: vertical;
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &::placeholder { color: var(--glad-text-muted); opacity: 1; }
  &:focus {
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.08);
  }
}

/* Advanced collapse */
.pb-insp__advanced {
  border-top: 1px solid var(--glad-line);
  padding-top: 0.65rem;
}
.pb-insp__advanced-summary {
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-ink);
  cursor: pointer;
  &::-webkit-details-marker { display: none; }
  svg { color: var(--glad-text-soft); transition: transform 0.15s ease; }
}
.pb-insp__advanced[open] .pb-insp__advanced-summary svg { transform: rotate(180deg); }
.pb-insp__advanced-body {
  padding: 0.5rem 0 0.25rem;
}

.pb-insp-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: 1.25rem 1.25rem 2rem;
  color: var(--glad-text-soft);
  gap: 0.4rem;
}
.pb-insp-empty__title {
  font-family: var(--glad-font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--glad-ink);
}
.pb-insp-empty__sub {
  font-size: 0.78rem;
  color: var(--glad-text-soft);
  line-height: 1.45;
  max-width: 240px;
}
.pb-insp-empty__icon-card {
  margin-top: 0.85rem;
  width: 100%;
  height: 78px;
  border-radius: 10px;
  background: var(--glad-paper-2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--glad-text-muted);
}

/* Inspector remove (text-button style) */
.pb-insp-remove {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  padding: 0.45rem 0.7rem;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-family: var(--glad-font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--glad-danger);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  &:hover { background: #fef2f2; }
  svg { color: currentColor; }
}

/* Canvas empty state — illustrated */
.pb-empty-state {
  max-width: 460px;
  margin: 2.5rem auto 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
}
.pb-empty-state__art {
  margin-bottom: 0.4rem;
  svg { display: block; width: 220px; height: auto; }
}
.pb-empty-state__title {
  font-family: var(--glad-font-sans);
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--glad-ink);
  letter-spacing: -0.01em;
}
.pb-empty-state__sub {
  font-size: 0.85rem;
  color: var(--glad-text-soft);
  line-height: 1.5;
  max-width: 360px;
}
.pb-empty-state__cta {
  margin-top: 0.85rem;
  height: 38px;
  padding: 0 1.1rem;
  font-size: 0.83rem;
}
.pb-empty-state__link {
  margin-top: 0.4rem;
  font-size: 0.83rem;
  color: var(--glad-text-soft);
}

/* ---------- BUTTONS ---------- */
button.pb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 34px;
  flex-shrink: 0;
  padding: 0 0.95rem;
  border-radius: 8px;
  font-family: var(--glad-font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  line-height: 1;
  letter-spacing: -0.005em;
  border: 1px solid var(--glad-ink);
  background: var(--glad-ink);
  color: #fff;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.18);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  svg { color: currentColor; }

  &.pb-btn--primary {
    background: var(--glad-ink);
    color: #fff;
    border-color: var(--glad-ink);
    &:hover:not(:disabled) { background: var(--glad-ink-2); border-color: var(--glad-ink-2); }
  }
  &.pb-btn--ghost {
    background: var(--glad-surface);
    color: var(--glad-ink);
    border-color: var(--glad-line);
    &:hover:not(:disabled) {
      background: var(--glad-paper);
      border-color: var(--glad-line-strong);
    }
  }
  &.pb-btn--icon {
    width: 34px;
    padding: 0;
    background: var(--glad-surface);
    color: var(--glad-text-soft);
    border-color: var(--glad-line);
    &:hover:not(:disabled) {
      background: var(--glad-paper);
      color: var(--glad-ink);
      border-color: var(--glad-line-strong);
    }
  }
  &.pb-btn--destructive {
    background: var(--glad-surface);
    color: var(--glad-danger);
    border-color: #fecaca;
    &:hover:not(:disabled) {
      background: #fef2f2;
      border-color: var(--glad-danger);
    }
  }
  &.pb-btn--sm {
    height: 28px;
    padding: 0 0.7rem;
    font-size: 0.76rem;
  }
  &.pb-btn--block {
    width: 100%;
    height: 38px;
  }
}

/* ---------- SORTABLE STATES ---------- */
.pb-sortable-ghost {
  opacity: 0.4;
  background: var(--glad-paper);
  border-radius: 10px;
}
.pb-sortable-chosen { cursor: grabbing; }
.pb-sortable-drag {
  background: var(--glad-surface);
  border: 1px solid var(--glad-ink);
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 17, 21, 0.12);
}

/* ---------- TOAST ---------- */
.pb-toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  background: var(--glad-ink);
  color: #fff;
  box-shadow: 0 8px 24px rgba(15, 17, 21, 0.18);
  z-index: 9999;
  animation: pb-toast-in 0.18s ease;
  &--ok    { background: var(--glad-ink); }
  &--warn  { background: var(--glad-accent); }
  &--error { background: var(--glad-danger); }
}
@keyframes pb-toast-in {
  from { transform: translate(-50%, 12px); opacity: 0; }
  to   { transform: translate(-50%, 0);    opacity: 1; }
}
</style>
